import { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import toast from 'react-hot-toast';
import {
  Sparkles,
  Clock,
  CheckCircle,
  AlertTriangle,
  Plus,
  User,
  Play,
  X,
  BedDouble,
  Wrench,
  Search,
  RefreshCw
} from 'lucide-react';
import {
  TAREAS_HOUSEKEEPING,
  RESUMEN_HOUSEKEEPING,
  CREAR_TAREA_HOUSEKEEPING,
  ACTUALIZAR_TAREA_HOUSEKEEPING,
  ASIGNAR_TAREA_HOUSEKEEPING
} from '../graphql/housekeeping';
import { GET_HABITACIONES } from '../graphql/habitaciones';
import { GET_USUARIOS } from '../graphql/configuracion';
import './Housekeeping.css';

const hoy = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });

const tipoLabels = {
  limpieza: 'Limpieza',
  mantenimiento: 'Mantenimiento',
  inspeccion: 'Inspeccion',
  limpieza_profunda: 'Limp. Profunda',
};

const prioridadLabels = {
  urgente: 'Urgente',
  alta: 'Alta',
  normal: 'Normal',
  baja: 'Baja',
};

const estadoLabels = {
  pendiente: 'Pendiente',
  en_progreso: 'En Progreso',
  completada: 'Completada',
  cancelada: 'Cancelada',
};

const Housekeeping = () => {
  const [fecha, setFecha] = useState(hoy());
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [showModal, setShowModal] = useState(false);

  // Queries
  const { data: tareasData, loading: loadingTareas, refetch: refetchTareas } = useQuery(TAREAS_HOUSEKEEPING, {
    variables: {
      fecha,
      ...(filtroEstado && { estado: filtroEstado }),
      ...(filtroTipo && { tipo: filtroTipo }),
    },
    pollInterval: 10000,
  });

  const { data: resumenData, refetch: refetchResumen } = useQuery(RESUMEN_HOUSEKEEPING, {
    variables: { fecha },
    pollInterval: 10000,
  });

  const { data: habData } = useQuery(GET_HABITACIONES);
  const { data: usersData } = useQuery(GET_USUARIOS, { variables: { activo: true } });

  // Mutations
  const [crearTarea] = useMutation(CREAR_TAREA_HOUSEKEEPING, {
    onCompleted: () => {
      toast.success('Tarea creada');
      setShowModal(false);
      refetchTareas();
      refetchResumen();
    },
    onError: (e) => toast.error(e.message),
  });

  const [actualizarTarea] = useMutation(ACTUALIZAR_TAREA_HOUSEKEEPING, {
    onCompleted: () => {
      toast.success('Tarea actualizada');
      refetchTareas();
      refetchResumen();
    },
    onError: (e) => toast.error(e.message),
  });

  const [asignarTarea] = useMutation(ASIGNAR_TAREA_HOUSEKEEPING, {
    onCompleted: () => {
      toast.success('Tarea asignada');
      refetchTareas();
      refetchResumen();
    },
    onError: (e) => toast.error(e.message),
  });

  const tareas = tareasData?.tareasHousekeeping || [];
  const resumen = resumenData?.resumenHousekeeping;
  const staffLimpieza = (usersData?.usuarios || []).filter(
    u => u.rol === 'limpieza' || u.rol === 'mantenimiento'
  );

  const handleMarcarCompletada = (tarea) => {
    actualizarTarea({ variables: { id: tarea.id, input: { estado: 'completada' } } });
  };

  const handleIniciarTarea = (tarea) => {
    actualizarTarea({ variables: { id: tarea.id, input: { estado: 'en_progreso' } } });
  };

  const handleAsignar = (tareaId, usuarioId) => {
    asignarTarea({ variables: { id: tareaId, usuario_id: parseInt(usuarioId) } });
  };

  if (loadingTareas && !tareasData) {
    return (
      <div className="housekeeping">
        <div className="hk__loading"><Clock size={18} /> Cargando housekeeping...</div>
      </div>
    );
  }

  return (
    <div className="housekeeping">
      {/* Header */}
      <div className="hk__header">
        <div className="hk__title-group">
          <h1 className="hk__title"><Sparkles size={22} /> Housekeeping</h1>
          <p className="hk__subtitle">Gestion de tareas de limpieza y mantenimiento</p>
        </div>
        <div className="hk__header-actions">
          <input
            type="date"
            className="hk__date-input"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
          />
          <button className="hk__btn" onClick={() => { refetchTareas(); refetchResumen(); }}>
            <RefreshCw size={14} />
          </button>
          <button className="hk__btn hk__btn--primary" onClick={() => setShowModal(true)}>
            <Plus size={14} /> Nueva Tarea
          </button>
        </div>
      </div>

      {/* Summary */}
      {resumen && (
        <div className="hk__summary">
          <div className="hk__stat">
            <div className="hk__stat-icon hk__stat-icon--yellow"><Clock size={18} /></div>
            <div className="hk__stat-info">
              <span className="hk__stat-value">{resumen.pendientes}</span>
              <span className="hk__stat-label">Pendientes</span>
            </div>
          </div>
          <div className="hk__stat">
            <div className="hk__stat-icon hk__stat-icon--blue"><Play size={18} /></div>
            <div className="hk__stat-info">
              <span className="hk__stat-value">{resumen.en_progreso}</span>
              <span className="hk__stat-label">En Progreso</span>
            </div>
          </div>
          <div className="hk__stat">
            <div className="hk__stat-icon hk__stat-icon--green"><CheckCircle size={18} /></div>
            <div className="hk__stat-info">
              <span className="hk__stat-value">{resumen.completadas}</span>
              <span className="hk__stat-label">Completadas</span>
            </div>
          </div>
          <div className="hk__stat">
            <div className="hk__stat-icon hk__stat-icon--red"><AlertTriangle size={18} /></div>
            <div className="hk__stat-info">
              <span className="hk__stat-value">{resumen.habitaciones_en_limpieza}</span>
              <span className="hk__stat-label">Hab. en Limpieza</span>
            </div>
          </div>
          <div className="hk__stat">
            <div className="hk__stat-icon hk__stat-icon--purple"><BedDouble size={18} /></div>
            <div className="hk__stat-info">
              <span className="hk__stat-value">{resumen.pendientes + resumen.en_progreso + resumen.completadas + resumen.canceladas}</span>
              <span className="hk__stat-label">Total Tareas</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="hk__filters">
        <select className="hk__filter-select" value={filtroEstado} onChange={(e) => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="pendiente">Pendiente</option>
          <option value="en_progreso">En Progreso</option>
          <option value="completada">Completada</option>
          <option value="cancelada">Cancelada</option>
        </select>
        <select className="hk__filter-select" value={filtroTipo} onChange={(e) => setFiltroTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          <option value="limpieza">Limpieza</option>
          <option value="mantenimiento">Mantenimiento</option>
          <option value="inspeccion">Inspeccion</option>
          <option value="limpieza_profunda">Limpieza Profunda</option>
        </select>
      </div>

      {/* Content: tasks grid + staff sidebar */}
      <div className="hk__content">
        {/* Tasks grid */}
        <div>
          {tareas.length === 0 ? (
            <div className="hk__empty">
              <Search size={24} style={{ marginBottom: '0.5rem', opacity: 0.5 }} />
              <p>No hay tareas para los filtros seleccionados</p>
            </div>
          ) : (
            <div className="hk__grid">
              {tareas.map((tarea) => (
                <TaskCard
                  key={tarea.id}
                  tarea={tarea}
                  staffLimpieza={staffLimpieza}
                  onCompletar={handleMarcarCompletada}
                  onIniciar={handleIniciarTarea}
                  onAsignar={handleAsignar}
                />
              ))}
            </div>
          )}
        </div>

        {/* Staff sidebar */}
        {resumen?.carga_por_usuario?.length > 0 && (
          <div className="hk__staff">
            <div className="hk__staff-header">Carga de Personal</div>
            <div className="hk__staff-list">
              {resumen.carga_por_usuario.map((s) => (
                <div key={s.usuario_id} className="hk__staff-item">
                  <span className="hk__staff-name">{s.nombre}</span>
                  <div className="hk__staff-counts">
                    {s.pendientes > 0 && <span className="hk__staff-count hk__staff-count--pending">{s.pendientes}</span>}
                    {s.en_progreso > 0 && <span className="hk__staff-count hk__staff-count--progress">{s.en_progreso}</span>}
                    {s.completadas > 0 && <span className="hk__staff-count hk__staff-count--done">{s.completadas}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* New task modal */}
      {showModal && (
        <NuevaTareaModal
          habitaciones={habData?.habitaciones || []}
          staffLimpieza={staffLimpieza}
          fecha={fecha}
          onCrear={(input) => crearTarea({ variables: { input } })}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};

// ============================================================================
// Task Card
// ============================================================================
const TaskCard = ({ tarea, staffLimpieza, onCompletar, onIniciar, onAsignar }) => {
  const isPendiente = tarea.estado === 'pendiente';
  const isEnProgreso = tarea.estado === 'en_progreso';
  const isCompletable = isPendiente || isEnProgreso;

  return (
    <div className="hk__card">
      <div className="hk__card-header">
        <div className="hk__card-room">
          <BedDouble size={16} style={{ color: 'var(--hotel-text-secondary)' }} />
          <span className="hk__card-room-num">{tarea.habitacion?.numero || '?'}</span>
          <span className="hk__card-room-type">{tarea.habitacion?.tipo || ''}</span>
        </div>
        <span className={`hk__card-priority hk__card-priority--${tarea.prioridad}`}>
          {prioridadLabels[tarea.prioridad] || tarea.prioridad}
        </span>
      </div>

      <div className="hk__card-body">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span className="hk__card-tipo">
            {tarea.tipo === 'mantenimiento' ? <Wrench size={13} /> : <Sparkles size={13} />}
            {tipoLabels[tarea.tipo] || tarea.tipo}
          </span>
          <span className={`hk__card-estado hk__card-estado--${tarea.estado}`}>
            {estadoLabels[tarea.estado] || tarea.estado}
          </span>
        </div>

        {tarea.usuario_asignado ? (
          <span className="hk__card-asignado">
            <User size={12} /> {tarea.usuario_asignado.nombre}
          </span>
        ) : (
          <span className="hk__card-asignado" style={{ color: '#f59e0b' }}>
            <User size={12} /> Sin asignar
          </span>
        )}

        {tarea.notas && <span className="hk__card-notas">{tarea.notas}</span>}
      </div>

      {isCompletable && (
        <div className="hk__card-actions">
          {isPendiente && (
            <button className="hk__btn hk__btn--sm" onClick={() => onIniciar(tarea)}>
              <Play size={12} /> Iniciar
            </button>
          )}
          <button className="hk__btn hk__btn--sm hk__btn--success" onClick={() => onCompletar(tarea)}>
            <CheckCircle size={12} /> Completar
          </button>
          {!tarea.usuario_asignado && staffLimpieza.length > 0 && (
            <select
              className="hk__filter-select"
              style={{ fontSize: '0.6875rem', padding: '0.125rem 0.25rem' }}
              defaultValue=""
              onChange={(e) => e.target.value && onAsignar(tarea.id, e.target.value)}
            >
              <option value="">Asignar...</option>
              {staffLimpieza.map(s => (
                <option key={s.id} value={s.id}>{s.nombre}</option>
              ))}
            </select>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Nueva Tarea Modal
// ============================================================================
const NuevaTareaModal = ({ habitaciones, staffLimpieza, fecha, onCrear, onClose }) => {
  const [form, setForm] = useState({
    habitacion_id: '',
    tipo: 'limpieza',
    prioridad: 'normal',
    usuario_asignado_id: '',
    notas: '',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.habitacion_id) {
      toast.error('Seleccione una habitacion');
      return;
    }
    onCrear({
      habitacion_id: parseInt(form.habitacion_id),
      tipo: form.tipo,
      prioridad: form.prioridad,
      usuario_asignado_id: form.usuario_asignado_id ? parseInt(form.usuario_asignado_id) : null,
      notas: form.notas || null,
      fecha,
    });
  };

  return (
    <div className="hk__modal-overlay" onClick={onClose}>
      <div className="hk__modal" onClick={(e) => e.stopPropagation()}>
        <div className="hk__modal-header">
          <span className="hk__modal-title">Nueva Tarea de Housekeeping</span>
          <button className="hk__modal-close" onClick={onClose}><X size={18} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="hk__modal-body">
            <div className="hk__field">
              <label>Habitacion *</label>
              <select value={form.habitacion_id} onChange={(e) => setForm(f => ({ ...f, habitacion_id: e.target.value }))}>
                <option value="">Seleccionar habitacion...</option>
                {habitaciones.map(h => (
                  <option key={h.id} value={h.id}>
                    {h.numero} - {h.tipo} (Piso {h.piso}) [{h.estado}]
                  </option>
                ))}
              </select>
            </div>
            <div className="hk__field">
              <label>Tipo</label>
              <select value={form.tipo} onChange={(e) => setForm(f => ({ ...f, tipo: e.target.value }))}>
                <option value="limpieza">Limpieza</option>
                <option value="limpieza_profunda">Limpieza Profunda</option>
                <option value="mantenimiento">Mantenimiento</option>
                <option value="inspeccion">Inspeccion</option>
              </select>
            </div>
            <div className="hk__field">
              <label>Prioridad</label>
              <select value={form.prioridad} onChange={(e) => setForm(f => ({ ...f, prioridad: e.target.value }))}>
                <option value="baja">Baja</option>
                <option value="normal">Normal</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
            <div className="hk__field">
              <label>Asignar a</label>
              <select value={form.usuario_asignado_id} onChange={(e) => setForm(f => ({ ...f, usuario_asignado_id: e.target.value }))}>
                <option value="">Sin asignar</option>
                {staffLimpieza.map(s => (
                  <option key={s.id} value={s.id}>{s.nombre} ({s.rol})</option>
                ))}
              </select>
            </div>
            <div className="hk__field">
              <label>Notas</label>
              <textarea
                placeholder="Observaciones..."
                value={form.notas}
                onChange={(e) => setForm(f => ({ ...f, notas: e.target.value }))}
              />
            </div>
          </div>
          <div className="hk__modal-footer">
            <button type="button" className="hk__btn" onClick={onClose}>Cancelar</button>
            <button type="submit" className="hk__btn hk__btn--primary">Crear Tarea</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Housekeeping;
